<template>
    <div>
        <div>
            <div>
                <h4 class="">
                    {{ qualityProvider.label }} Quality Checks
                </h4>
                <hr class="mb-4">
                <!--<div class="col col-4 align-right text-end">-->
                    <!--<button class="btn btn-primary">Add</button>-->
                <!--</div>-->
            </div>
            <div v-if="canCreateQualityChecks">
                <div v-for="c in checks" :key="c.id" >
                    <QualityCheckForm
                        :c="c"
                        :get-severity="getSeverity"
                        :network-activity="networkActivity"
                        :save="save"
                        :is-new-check="false"
                        :provider-config="qualityProvider"
                    />
                </div>
                <div v-if="isAddingNewCheck">
                    <QualityCheckForm
                        :c="newCheck"
                        :get-severity="getSeverity"
                        :network-activity="networkActivity"
                        :save="submitNewCheck"
                        :is-new-check="true"
                        :provider-config="qualityProvider"
                    />
                </div>
                <!-- DEV-2885: hidden once the provider's cap is reached. Theta Lake allows exactly
                     one check, because one SWRV rule binds one integration to one workflow and one
                     policy on their side. Proofpoint has no cap, so this is always shown for it. -->
                <button v-if="canAddCheck" class="btn btn-primary" :disabled="networkActivity" @click="showNewCheckForm">
                    Add new quality check
                </button>
                <p v-else-if="atCheckLimit" class="text-muted small mb-0">
                    {{ qualityProvider.label }} supports one quality check, which is configured above.
                </p>
            </div>
            <div v-else class="text-muted">
                <p v-if="promptProviderAuthentication">
                    Connect {{ qualityProvider.label }} to create quality checks. Go to
                    <strong>Integrations</strong> in the settings menu and connect
                    {{ qualityProvider.label }}, then come back to this tab.
                </p>
                <p v-else-if="promptSales">
                    Quality checks are not included in your current plan. Contact us to add
                    {{ qualityProvider.label }} compliance checking.
                </p>
                <p v-else>
                    {{ qualityProvider.label }} quality checks are unavailable for this account.
                </p>
            </div>
        </div>
    </div>
</template>

<style>

.em1 {
    font-size: inherit !important;
}
.integration-list {
    list-style-type: none;
    padding-left: 0;
}
.integration-list .btn {
    margin-left: 10px;
}

</style>

<script>
import HasGlobalQualityCheckList from '@/components/QualityChecks/HasGlobalQualityCheckList'
import * as toastr from "toastr";
import IntegrationLoader from '@/components/DataManagement/IntegrationLoader'
import {mapGetters} from 'vuex'
import QualityCheckForm from "@/components/QualityChecks/QualityCheckForm.vue";
import {
    SEVERITY,
    severityLabel,
    providerConfig,
    DEFAULT_QUALITY_CHECK_PROVIDER,
} from '@/components/QualityChecks/qualityCheckProviders';

    export default {
        components: {QualityCheckForm},
        mixins: [HasGlobalQualityCheckList, IntegrationLoader],
        props: {
            account: Object,
            provider: { type: String, default: DEFAULT_QUALITY_CHECK_PROVIDER },
        },
        async mounted() {
            this.load();
        },
        computed: {
            ...mapGetters({
                currentAccountOverview: 'currentAccountOverview',
            }),
            promptProviderAuthentication() {
                return !this.authenticatedProvider
                    && this.featuresForAccount[this.qualityProvider.featureKey]
                    && this.featuresForAccount.qualityChecks
            },
            promptSales() {
                return !this.featuresForAccount[this.qualityProvider.featureKey] || !this.featuresForAccount.qualityChecks
            },
            atCheckLimit() {
                const max = this.qualityProvider.maxChecks;
                if (!max) return false;
                return this.checks.length + (this.isAddingNewCheck ? 1 : 0) >= max;
            },
            canAddCheck() {
                return !this.isAddingNewCheck && !this.atCheckLimit;
            }

        },
        data() {
            return {
                checks: [],
                networkActivity: false,
                newCheck: this.blankCheck(),
                isAddingNewCheck: false,
            }
        },
        watch: {
            account() {
                this.load()
            },
            provider() {
                this.newCheck = this.blankCheck();
                this.load()
            }
        },
        methods: {
            blankCheck() {
                const config = providerConfig(this.provider);
                return {
                    newName: null,
                    newId: null,
                    meta: config.newCheckMeta(),
                    default: false,
                    statuses: config.statuses.map(s => ({...s})),
                };
            },
            async load() {
                this.reloadIntegrationData();
                this.checks = this.$sort.alphabetize(await this.fetchChecksAndInitializeDoNotExist(), 'created').map(c => ({...c, statuses: this.$sort.alphabetize(c.statuses, 'severity')}))
            },
            getSeverity(key) {
                return severityLabel(key);
            },

            integrationByName(name) {
                return this.metaIntegrations[name]
            },
            async save(check) {
                console.log('* QualityChecks save check', check)
                this.networkActivity = 'Saving...';
                if (providerConfig(check.type || this.provider).collapseErrorOntoInconclusive) {
                    let error = check.statuses.find(x => x.severity === SEVERITY.ERROR);
                    let inconclusive = check.statuses.find(x => x.severity === SEVERITY.INCONCLUSIVE);
                    if (error && inconclusive) {
                        error.status_label = inconclusive.status_label;
                    }
                }
                await this.$api.put(`/quality-checks/${check.id}`, check);
                await this.$api.put(`/quality-checks/${check.id}/statuses`, check)
                this.networkActivity = false;
                toastr.success("Saved quality check successfully!")
            },
            showNewCheckForm() {
                this.isAddingNewCheck = true;
            },
            async submitNewCheck() {
                this.networkActivity = true;
                try {
                    const body = {
                        name: this.newCheck.name,
                        default: this.newCheck.default,
                        type: this.qualityProvider.key,
                        // Send the form's own meta rather than picking named fields out of it. The
                        // provider decided the shape in newCheckMeta() and the form only edits the
                        // inputs that provider renders, so this stays correct as fields differ.
                        meta: this.newCheck.meta
                    };
                    await this.$api.post(`/accounts/${this.currentAccountOverview.id}/quality-checks`, body);
                    toastr.success("Successfully added new quality check!");
                    this.isAddingNewCheck = false;
                    this.newCheck = this.blankCheck();
                    await this.load(); // refresh the list
                } catch (e) {
                    console.error('Error adding new Quality check:', e)
                    toastr.error("Failed to add new quality check.");
                } finally {
                    this.networkActivity = false;
                }
            },
        }

    }
</script>
